import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'gap-2 inline-flex items-center justify-center whitespace-nowrap rounded-full text-sm font-medium cursor-pointer transition-colors focus-visible:outline-hidden disabled:pointer-events-none disabled:bg-disabled disabled:text-muted-foreground disabled:[&>div[data-is-icon=true]]:bg-muted-foreground disabled:[&>img[data-is-icon=true]]:hidden disabled:[&>div[data-is-icon=true]]:block',
  {
    variants: {
      size: {
        medium: 'h-10 px-4 py-1 text-xs font-semibold',
        large: 'h-[50px] px-6 py-1 text-base',
        icon: 'h-9 w-9 px-2 py-2',
      },
      variant: {
        primary:
          'bg-primary text-primary-foreground hover:bg-primary-hover [&>[data-is-icon=true]]:bg-primary-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary-hover [&>[data-is-icon=true]]:bg-secondary-foreground',
        tertiary: 'bg-white-400 text-black hover:bg-white-200',
        destructive:
          'bg-destructive text-destructive-foreground [&>[data-is-icon=true]]:bg-destructive-foreground hover:bg-destructive-hover',
        'destructive-ghost':
          'bg-transparent text-destructive [&>[data-is-icon=true]]:bg-destructive hover:text-destructive-500 hover:[&>[data-is-icon=true]]:bg-destructive-500 hover:bg-destructive-100',
        ghost: 'bg-transparent hover:border-white-600',
        outline:
          'border border-black bg-transparent shadow-xs hover:bg-black hover:text-white [&>[data-is-icon=true]]:bg-white disabled:border-disabled disabled:bg-background',
        link: 'font-normal underline-offset-4 underline hover:underline px-0 py-0 h-5 border-0 disabled:bg-transparent disabled:no-underline',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'large',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  loadingText?: string;
  size?: 'medium' | 'large' | 'icon';
  variant?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'destructive'
    | 'ghost'
    | 'outline'
    | 'link'
    | 'destructive-ghost';
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      loadingText = 'Loading...',
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className='h-4 w-4 animate-spin' />
            {loadingText}
          </>
        ) : (
          props.children
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
